import {
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { boolean, object } from 'yup';

import ConsentSurvey from '@components/ConsentSurvey';
import ContentLoader from '@components/ContentLoader';
import RoundedButton from '@components/RoundedButton/index';

import { VITALL_PRIVACY, VITALL_TERMS } from '@constants/external-links';

import { useCreateConsent } from '@hooks/api/consent';

import { getVitallConsentTemplate } from '@models/fhir-templates/consent';

import { consentLanguage } from '@views/ManageAccount/Consent/consentLanguage';

const messages = defineMessages({
  recordCollectionAndStorage: {
    id: 'consentForm.recordCollectionAndStorage',
    defaultMessage: 'VITALL Record Collection and Storage Consent',
  },
  vitallMessage: {
    id: 'consentForm.vitallMessage',
    defaultMessage:
      'I{hasName, select, true {, <b>{name}</b>,} other {}} consent to VITALL collecting, storing, and processing my Personally Identifiable Information (“PII”) and Personal Health Information (“PHI”) in accordance with its Terms and Conditions and its Privacy Notice to provide its services.',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  helperText: {
    id: 'assessment.bha.consent.helperText',
    defaultMessage:
      "This consent is required to use VITALL's services. After clicking submit, a QR code will appear to begin the assessment.",
  },
  practitionerConsenting: {
    id: 'assessment.bha.consent.practitionerConsenting',
    defaultMessage:
      '<b>Practitioners:</b> Ensure that the patient has directly communicated their agreement to the following consent in your presence.',
  },
});

interface ConsentFormProps {
  /** Patient first and last name */
  name?: string | null;
  patient_id: string;
  onDidConsent?: (didConsent: boolean) => void;
}

interface FormValues {
  record_collection: boolean;
}

export default function ConsentForm({
  name,
  patient_id,
  onDidConsent,
}: ConsentFormProps) {
  const { formatMessage } = useIntl();

  const [createConsent, createConsentLoading] = useCreateConsent();

  const handleDidConsent = () => {
    onDidConsent?.(true);
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    await createConsent({
      input: {
        ...getVitallConsentTemplate(patient_id, values.record_collection),
      },
    });
    handleDidConsent();
    setSubmitting(false);
  };

  const initialValues: FormValues = {
    record_collection: false,
  };

  const consentFormSchema: Schema<FormValues> = object().shape({
    record_collection: boolean()
      .oneOf([true])
      .required(formatMessage(messages.required)),
  });

  const hasName = Boolean(name);

  return (
    <>
      <DialogTitle sx={{ px: 6, pb: 0, fontSize: 20 }}>
        {formatMessage(messages.recordCollectionAndStorage)}
      </DialogTitle>

      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={consentFormSchema}
        >
          {({ errors, isSubmitting }) => {
            const loading = createConsentLoading || isSubmitting;
            return (
              <Form>
                <ContentLoader loading={loading}>
                  <Typography
                    sx={{
                      px: 3,
                      pt: 2,
                      pb: 1,
                    }}
                  >
                    {formatMessage(messages.practitionerConsenting)}
                  </Typography>
                  <ConsentSurvey
                    error={
                      errors.record_collection
                        ? formatMessage(messages.required)
                        : undefined
                    }
                    links={[
                      {
                        text: 'Terms and Conditions',
                        link: VITALL_TERMS,
                      },
                      {
                        text: 'Privacy Notice',
                        link: VITALL_PRIVACY,
                      },
                    ]}
                    name="record_collection"
                    text={formatMessage(messages.vitallMessage, {
                      hasName,
                      name,
                    })}
                    validate
                  />
                </ContentLoader>

                <RoundedButton
                  disabled={loading || Boolean(errors.record_collection)}
                  type="submit"
                >
                  {formatMessage(consentLanguage.submit)}
                </RoundedButton>

                <DialogContentText variant="body2">
                  {formatMessage(messages.helperText)}
                </DialogContentText>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </>
  );
}
